import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '@/store'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { superadminApi } from '@/services/api/superadmin'
import { teacherImportApi } from '@/services/api/teacher'

type PeriodType = 'school_holiday' | 'public_holiday' | 'special_day'

interface Period {
  id: string
  school_id: string
  name: string
  start_date: string
  end_date: string
  start_time?: string | null
  end_time?: string | null
  period_type: string
  faculty_ids?: string[] | null
  year_numbers?: number[] | null
  school_year?: string | null
  manual_locked?: boolean
  import_source?: string | null
}

interface Meta {
  can_write: boolean
  kanton_code: string | null
  kanton_enabled: boolean
  year_numbers: number[]
  school_years: string[]
  current_school_year: string | null
  year_starts?: Record<string, string>
}

interface PreviewPeriod {
  name: string
  start_date: string
  end_date: string
  period_type?: string
  status?: string
  skip_reason?: string | null
}

const TYPE_LABEL: Record<string, string> = {
  school_holiday: 'Ferien',
  public_holiday: 'Feiertag',
  special_day: 'Sonder',
}

function fmtTime(t?: string | null) {
  if (!t) return ''
  return t.slice(0, 5)
}

export function HolidayPeriodList() {
  const user = useAuthStore((s) => s.user)
  const schoolId = user?.school_id
  const [meta, setMeta] = useState<Meta | null>(null)
  const [year, setYear] = useState<string>('')
  const [startDate, setStartDate] = useState('')
  const [rows, setRows] = useState<Period[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Period | null>(null)
  const [faculties, setFaculties] = useState<{ id: string; name: string; code?: string }[]>([])
  const [preview, setPreview] = useState<{ periods: PreviewPeriod[]; file?: File } | null>(null)

  const [form, setForm] = useState({
    name: '',
    period_type: 'school_holiday' as PeriodType,
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    faculty_ids: [] as string[],
    year_numbers: [] as number[],
  })

  const load = useCallback(async () => {
    const m = await handleApiRequest(apiClient.get('/api/v1/holidays/meta')) as Meta
    setMeta(m)
    const y = year || m.current_school_year || m.school_years[0] || ''
    if (!year && y) setYear(y)
    const starts = m.year_starts || {}
    const key = y || year
    setStartDate(key && starts[key] ? starts[key] : '')
    const params: Record<string, string> = {}
    if (schoolId) params.school_id = schoolId
    if (y) params.school_year = y
    const list = await handleApiRequest(apiClient.get('/api/v1/holidays/periods', { params })) as Period[]
    setRows(list)
  }, [schoolId, year])

  useEffect(() => { load().catch((e) => setError(String(e))) }, [load])
  useEffect(() => {
    const applyFac = (r: unknown) => {
      const list = Array.isArray(r) ? r : (r as { faculties?: unknown[] })?.faculties
      if (Array.isArray(list)) setFaculties(list as { id: string; name: string; code?: string }[])
    }
    superadminApi.listFaculties().then(applyFac).catch(() => teacherImportApi.listFaculties().then(applyFac).catch(() => undefined))
  }, [])

  const canWrite = Boolean(meta?.can_write)
  const years = useMemo(() => meta?.school_years?.length ? meta.school_years : (year ? [year] : []), [meta, year])

  function openNew() {
    setEditing(null)
    setForm({ name: '', period_type: 'school_holiday', start_date: '', end_date: '', start_time: '', end_time: '', faculty_ids: [], year_numbers: [] })
    setShowForm(true)
  }
  function openEdit(p: Period) {
    setEditing(p)
    setForm({
      name: p.name,
      period_type: (p.period_type as PeriodType) || 'school_holiday',
      start_date: p.start_date,
      end_date: p.end_date,
      start_time: fmtTime(p.start_time),
      end_time: fmtTime(p.end_time),
      faculty_ids: (p.faculty_ids || []).map(String),
      year_numbers: p.year_numbers || [],
    })
    setShowForm(true)
  }

  async function saveForm() {
    if (!schoolId) return
    setBusy(true)
    setError(null)
    const payload = {
      school_id: schoolId,
      name: form.name.trim(),
      period_type: form.period_type,
      start_date: form.start_date,
      end_date: form.end_date || form.start_date,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      faculty_ids: form.period_type === 'special_day' ? form.faculty_ids : [],
      year_numbers: form.period_type === 'special_day' ? form.year_numbers : [],
    }
    try {
      if (editing) await handleApiRequest(apiClient.patch(`/api/v1/holidays/periods/${editing.id}`, payload))
      else await handleApiRequest(apiClient.post('/api/v1/holidays/periods', payload))
      setShowForm(false)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  async function deleteSelected() {
    if (!selected.size) return
    setBusy(true)
    try {
      await handleApiRequest(apiClient.post('/api/v1/holidays/periods/bulk-delete', { ids: [...selected] }))
      setSelected(new Set())
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  async function saveStart() {
    if (!year || !startDate) return
    setBusy(true)
    try {
      await handleApiRequest(apiClient.put(`/api/v1/holidays/school-years/${encodeURIComponent(year)}/start`, { start_date: startDate }))
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Start-Datum fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  async function runSilent(path: string, label: string) {
    setBusy(true)
    setError(null)
    try {
      const res = await handleApiRequest(apiClient.post(path)) as { periods_created?: number; periods_skipped?: number }
      setToast(`${label}: ${res.periods_created ?? 0} angelegt, ${res.periods_skipped ?? 0} übersprungen`)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : `${label} fehlgeschlagen`)
    } finally {
      setBusy(false)
    }
  }

  async function onFile(f: File) {
    const fd = new FormData()
    fd.append('file', f)
    setBusy(true)
    try {
      const res = await handleApiRequest(apiClient.post('/api/v1/holidays/import/file/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } })) as { periods: PreviewPeriod[] }
      setPreview({ periods: res.periods, file: f })
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Preview fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  async function executePreview() {
    if (!preview?.file) return
    const fd = new FormData()
    fd.append('file', preview.file)
    setBusy(true)
    try {
      const res = await handleApiRequest(apiClient.post('/api/v1/holidays/import/file/execute', fd, { headers: { 'Content-Type': 'multipart/form-data' } })) as { periods_created?: number; periods_skipped?: number }
      setToast(`Datei: ${res.periods_created ?? 0} angelegt, ${res.periods_skipped ?? 0} übersprungen`)
      setPreview(null)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Import fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="px-4 lg:px-6 py-4 border-b bg-white space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold">Ferien, Feiertage, Sondertage</h2>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={openNew} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg">Neu</button>
            <label className="px-3 py-1.5 text-sm border rounded-lg cursor-pointer">
              Datei
              <input type="file" className="hidden" accept=".csv,.xlsx,.xls,.pdf" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
            </label>
            <button type="button" disabled={busy} onClick={() => runSilent('/api/v1/holidays/import/evento', 'Evento')} className="px-3 py-1.5 text-sm border rounded-lg">Evento</button>
            <button type="button" disabled={busy || !meta?.kanton_enabled} onClick={() => runSilent('/api/v1/holidays/import/kanton', 'Kanton')} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50" title={meta?.kanton_enabled ? '' : 'Region der Schule setzen'}>Kanton</button>
            {selected.size > 0 && (
              <button type="button" onClick={deleteSelected} className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg">Löschen ({selected.size})</button>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <label className="flex items-center gap-2">
          Schuljahr
          <select value={year} onChange={(e) => setYear(e.target.value)} className="border rounded px-2 py-1">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
            {!years.length && <option value="">—</option>}
          </select>
        </label>
        <label className="flex items-center gap-2">
          Start
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded px-2 py-1" disabled={!canWrite} />
          {canWrite && <button type="button" onClick={saveStart} className="text-indigo-600">Speichern</button>}
        </label>
        {canWrite && !meta?.kanton_enabled && (
          <span className="text-xs text-gray-500">Kanton: Region der Schule setzen (nur CH)</span>
        )}
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      {toast && <div className="text-sm text-green-700">{toast}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              {canWrite && <th className="w-8" />}
              <th className="py-2">Name</th>
              <th>Typ</th>
              <th>Von</th>
              <th>Bis</th>
              <th>Uhr</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b hover:bg-gray-50">
                {canWrite && (
                  <td>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={(e) => {
                      const n = new Set(selected)
                      if (e.target.checked) n.add(p.id); else n.delete(p.id)
                      setSelected(n)
                    }} />
                  </td>
                )}
                <td className="py-2">
                  {canWrite ? <button type="button" className="text-indigo-700" onClick={() => openEdit(p)}>{p.name}</button> : p.name}
                </td>
                <td>{TYPE_LABEL[p.period_type] || p.period_type}</td>
                <td>{p.start_date}</td>
                <td>{p.end_date}</td>
                <td>{[fmtTime(p.start_time), fmtTime(p.end_time)].filter(Boolean).join('–') || '—'}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="py-4 text-gray-500">Keine Einträge in diesem Schuljahr</td></tr>}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="border rounded-lg p-3 space-y-2">
          <div className="font-medium text-sm">Preview</div>
          {preview.periods.map((p, i) => (
            <div key={i} className="text-sm">{p.name} {p.start_date}–{p.end_date} · {p.status}{p.skip_reason ? ` (${p.skip_reason})` : ''}</div>
          ))}
          <div className="flex gap-2">
            <button type="button" onClick={executePreview} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg">Übernehmen</button>
            <button type="button" onClick={() => setPreview(null)} className="px-3 py-1.5 text-sm border rounded-lg">Abbrechen</button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 w-full max-w-md space-y-3">
            <h3 className="font-semibold">{editing ? 'Bearbeiten' : 'Neu'}</h3>
            <label className="block text-sm">Name
              <input className="w-full border rounded px-2 py-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="block text-sm">Typ
              <select className="w-full border rounded px-2 py-1" value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value as PeriodType })}>
                <option value="school_holiday">Ferien</option>
                <option value="public_holiday">Feiertag</option>
                <option value="special_day">Sonder</option>
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm">Von<input type="date" className="w-full border rounded px-2 py-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
              <label className="text-sm">Bis<input type="date" className="w-full border rounded px-2 py-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
              <label className="text-sm">Uhr ab<input type="time" className="w-full border rounded px-2 py-1" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></label>
              <label className="text-sm">Uhr bis<input type="time" className="w-full border rounded px-2 py-1" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></label>
            </div>
            {form.period_type === 'special_day' && (
              <div className="space-y-2 text-sm">
                <div>Fakultäten
                  <div className="flex flex-wrap gap-1 mt-1">
                    {faculties.map((f) => (
                      <label key={f.id} className="flex items-center gap-1 border rounded px-2 py-0.5">
                        <input type="checkbox" checked={form.faculty_ids.includes(f.id)} onChange={(e) => {
                          const n = e.target.checked ? [...form.faculty_ids, f.id] : form.faculty_ids.filter((x) => x !== f.id)
                          setForm({ ...form, faculty_ids: n })
                        }} />
                        {f.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div>Zahlen
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(meta?.year_numbers || []).map((n) => (
                      <label key={n} className="flex items-center gap-1 border rounded px-2 py-0.5">
                        <input type="checkbox" checked={form.year_numbers.includes(n)} onChange={(e) => {
                          const next = e.target.checked ? [...form.year_numbers, n] : form.year_numbers.filter((x) => x !== n)
                          setForm({ ...form, year_numbers: next })
                        }} />
                        {n}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm border rounded-lg">Abbrechen</button>
              <button type="button" disabled={busy || !form.name || !form.start_date} onClick={saveForm} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-50">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
